import {
  InspectionEvent,
  AnyMachineSnapshot,
  InspectedSnapshotEvent,
} from 'xstate5'

// Local utils

const isInspectedSnapshotEvent = (
  event: InspectionEvent,
): event is InspectedSnapshotEvent => event.type === '@xstate.snapshot'

const getNewStepNumber = (
  currentStep: string,
  stepDelta: number,
  subStepDelta: number,
) => {
  const [step, subStep = 0] = (currentStep || '0').split('.').map(Number)
  const newStep = step + stepDelta
  const newSubStep = stepDelta ? 0 : subStep + subStepDelta

  if (newSubStep > 0) {
    // Checks if newStep exists and > 0, to cover the case when quiz starts on a interstitial state.
    return `${newStep || 1}.${newSubStep}`
  }

  return `${newStep}`
}

/**
 * XState v5 Inspect API implementation for automated step and state history tracking
 *
 * This inspector automatically tracks:
 * - `step`, `stepHistory` and `stateHistory` based on next/back events and state tags
 *
 */
export function createStateHistoryInspector() {
  let previousStateName: string | null = null

  return (inspectionEvent: InspectionEvent) => {
    if (isInspectedSnapshotEvent(inspectionEvent)) {
      const actor = inspectionEvent.actorRef
      const snapshot = inspectionEvent.snapshot as AnyMachineSnapshot
      const eventType = inspectionEvent.event.type
      const currentStateName = snapshot.value as string
      const { context } = snapshot

      const isNextEvent = eventType === 'next'
      const isBackEvent = eventType === 'back'

      const stateNameChanged = currentStateName !== previousStateName
      previousStateName = currentStateName // Update for next iteration

      if (isNextEvent) {
        const hasInteractiveTag = snapshot.hasTag('interactive')
        const hasInterstitialTag = snapshot.hasTag('interstitial')

        if (hasInteractiveTag || hasInterstitialTag) {
          const newStep = stateNameChanged
            ? getNewStepNumber(
                context.step,
                hasInteractiveTag ? 1 : 0,
                hasInterstitialTag ? 1 : 0,
              )
            : context.step

          actor.send({
            type: 'updateStepHistory',
            step: newStep,
            stepHistory: [...context.stepHistory, newStep],
            stateHistory: [...context.stateHistory, currentStateName],
          })
        }
      }

      if (isBackEvent) {
        actor.send({
          type: 'updateStepHistory',
          step: context.stepHistory.at(-2) || '0',
          stepHistory: [...context.stepHistory.slice(0, -1)],
          stateHistory: [...context.stateHistory.slice(0, -1)],
        })
      }
    }
  }
}
